/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import IDate from '@dvrd/idate';

import {BOL_REVIEWS_URL_FRAGMENT, ISO_DATE_FORMAT} from './constants';

import {PeriodLabel, StatCardDataType} from './enums';

import {
    OverviewPeriodRange,
    OverviewTopStatsAds,
    OverviewTopStatsGeneral,
    OverviewTopStatsProfit,
    StatCardData
} from './interfaces';

type Translate = (key: string, fallback: string) => string;

const PERCENTAGE_DECIMALS = 1;

function roundTo(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}

/** Share of `total` taken by `part`, as a percentage. A zero total yields 0 —
 *  an account without revenue must still render its cards. */
export function shareOfTotal(part: number, total: number): number {
    if (!total) return 0;
    return roundTo((part / total) * 100, PERCENTAGE_DECIMALS);
}

export function toPeriodRange(start: IDate, end: IDate): OverviewPeriodRange {
    return {start_date: start.format(ISO_DATE_FORMAT), end_date: end.format(ISO_DATE_FORMAT)};
}

/** The backend already appends bol's reviews text fragment to overview product
 *  URLs — those pass through untouched; a fragmentless URL gets the same one. */
export function bolReviewsUrl(productUrl: string | null | undefined): string | null {
    if (!productUrl) return null;
    if (productUrl.includes('#')) return productUrl;
    return `${productUrl}${BOL_REVIEWS_URL_FRAGMENT}`;
}

/**
 * What the KPI cards compare against. The backend always returns the window
 * immediately preceding the selected one, so the label names that window in the
 * user's terms instead of a bare "previous": a day-long selection compares with
 * the day before, a week with the week before.
 *
 * Driven by the preset the user picked rather than the raw day count, because
 * "last month" and "last 30 days" are the same length but read differently.
 * A custom range (null preset) has no natural name, so it stays generic.
 */
export function comparisonLabel(preset: PeriodLabel | null, t: Translate): string {
    switch (preset) {
        case PeriodLabel.TODAY:
        case PeriodLabel.YESTERDAY:
            return t('overview.comparison.dayBefore', 'Dag ervoor');
        case PeriodLabel.LAST_7_DAYS:
        case PeriodLabel.LAST_WEEK:
            return t('overview.comparison.weekBefore', 'Week ervoor');
        case PeriodLabel.LAST_30_DAYS:
        case PeriodLabel.LAST_MONTH:
            return t('overview.comparison.monthBefore', 'Maand ervoor');
        default:
            return t('overview.comparison.periodBefore', 'Periode ervoor');
    }
}

/**
 * The KPI cards of the overview design, in design order. They are fed by three
 * endpoints (general / profit / ads, overview-openapi v0.0.8) that resolve
 * independently, so each block is built on its own and a slow one does not hold
 * up the rest.
 *
 * BluemateStatCard derives trend, delta and percentage itself from
 * value/prevValue, so this only maps the API snapshots onto card data and
 * computes the alternates the API does not send.
 */
export function buildGeneralStatCards(
    stats: OverviewTopStatsGeneral,
    t: Translate
): Array<Array<StatCardData> | StatCardData> {
    const {values, previous} = stats;

    return [
        [
            {
                label: t('overview.kpi.revenue', 'Omzet'),
                value: values.revenue,
                prevValue: previous.revenue,
                valueType: StatCardDataType.CURRENCY,
                hidePercentageToggle: true
            },
            {
                label: t('overview.kpi.revenueExclVat', 'Omzet (excl. btw)'),
                value: values.revenue_excl_vat,
                prevValue: previous.revenue_excl_vat,
                valueType: StatCardDataType.CURRENCY
            }
        ],
        {
            label: t('overview.kpi.orders', 'Aantal orders'),
            value: values.order_count,
            prevValue: previous.order_count,
            noToggles: true
        },
        {
            label: t('overview.kpi.returns', 'Retouren'),
            value: values.return_count,
            prevValue: previous.return_count,
            invertTrend: true,
            alternates: [
                {
                    label: t('overview.kpi.returnRate', 'Retour %'),
                    value: shareOfTotal(values.return_count, values.order_count),
                    prevValue: shareOfTotal(previous.return_count, previous.order_count),
                    valueType: StatCardDataType.PERCENTAGE,
                    invertTrend: true
                }
            ]
        },
        {
            label: t('overview.kpi.reviews', 'Nieuwe reviews'),
            value: values.review_count,
            prevValue: previous.review_count,
            noToggles: true
        },
        {
            label: t('overview.kpi.uploads', 'Facturen geüpload'),
            value: values.upload_count,
            prevValue: previous.upload_count,
            noToggles: true
        },
        {
            label: t('overview.kpi.reviewClicks', 'Review kliks'),
            value: values.review_clicks,
            prevValue: previous.review_clicks,
            noToggles: true
        },
        {
            label: t('overview.kpi.mailsSent', 'Mails verstuurd'),
            value: values.mail_count,
            prevValue: previous.mail_count,
            noToggles: true
        }
    ];
}

export function buildProfitStatCards(stats: OverviewTopStatsProfit, t: Translate): Array<StatCardData> {
    const {values, previous} = stats;

    return [
        {
            label: t('overview.kpi.profit', 'Winst'),
            value: values.profit,
            prevValue: previous.profit,
            valueType: StatCardDataType.CURRENCY,
            noToggles: true,
            info: t('profitInfo', 'De winst wordt enkel berekend met producten waarvoor de kosten zijn ingesteld.'),
            zeroAsDash: true
        },
        {
            label: t('overview.kpi.margin', 'Winstmarge'),
            value: values.margin,
            prevValue: previous.margin,
            valueType: StatCardDataType.PERCENTAGE,
            noToggles: true,
            info: t('profitInfo', 'De winst wordt enkel berekend met producten waarvoor de kosten zijn ingesteld.'),
            zeroAsDash: true
        }
    ];
}

/** Revenue is needed for the "% of total" alternates, but it belongs to the
 *  general block — when that one has not resolved yet the shares would all read
 *  0%, so the alternates are only attached once it is in. */
export function buildAdsStatCards(
    stats: OverviewTopStatsAds,
    general: OverviewTopStatsGeneral | null,
    t: Translate
): Array<StatCardData> {
    const {values, previous} = stats;
    const shareAlternate = (value: number | null, prevValue: number | null): StatCardData | undefined => {
        if (!general) return undefined;
        return {
            label: t('overview.kpi.shareOfTotal', '% van totale omzet'),
            value: value === null ? null : shareOfTotal(value, general.values.revenue),
            prevValue: prevValue === null ? null : shareOfTotal(prevValue, general.previous.revenue),
            valueType: StatCardDataType.PERCENTAGE
        };
    };

    const organicRevenue = general ? general.values.revenue - values.ads_revenue : null;
    const prevOrganicRevenue = general ? general.previous.revenue - previous.ads_revenue : null;
    return [
        {
            label: t('overview.kpi.organicRevenue', 'Omzet organisch'),
            value: organicRevenue,
            prevValue: prevOrganicRevenue,
            valueType: StatCardDataType.CURRENCY,
            alternates: [shareAlternate(organicRevenue, prevOrganicRevenue)].filter(data => data !== undefined)
        },
        {
            label: t('overview.kpi.adsRevenue', 'Omzet via ads'),
            value: values.ads_revenue,
            prevValue: previous.ads_revenue,
            valueType: StatCardDataType.CURRENCY,
            alternates: [shareAlternate(values.ads_revenue, previous.ads_revenue)].filter(data => data !== undefined)
        },
        {
            label: t('overview.kpi.adSpend', 'Ad spend'),
            value: values.ad_spend,
            prevValue: previous.ad_spend,
            valueType: StatCardDataType.CURRENCY,
            invertTrend: true,
            noToggles: true
        }
    ];
}

/** Where the general block splits in the design's card order: the first three
 *  open the grid, the rest close it after the profit and ads blocks. */
const GENERAL_HEAD_CARDS = 3;

/**
 * The twelve cards in the order the design lays them out, which interleaves the
 * three endpoints. A block that has not resolved yet contributes nothing and the
 * others keep their places.
 */
export function buildStatCards(
    general: OverviewTopStatsGeneral | null,
    profit: OverviewTopStatsProfit | null,
    ads: OverviewTopStatsAds | null,
    t: Translate
): Array<Array<StatCardData> | StatCardData> {
    const generalCards = general ? buildGeneralStatCards(general, t) : [];
    const profitCards = profit ? buildProfitStatCards(profit, t) : [];
    const adsCards = ads ? buildAdsStatCards(ads, general, t) : [];

    return [
        ...generalCards.slice(0, GENERAL_HEAD_CARDS),
        ...profitCards,
        ...adsCards,
        ...generalCards.slice(GENERAL_HEAD_CARDS)
    ];
}
